import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { collection, getDocs, limit, orderBy, query } from 'firebase/firestore';
import { db } from '../../firebase';
import { isPedalNewsUiEnabled } from '../../featureFlags';
import { formatNewsDateShort, newsArticleToJsDate } from '../../pages/newsUtils';
import './DashboardPedalNewsStrip.css';

const FETCH_LIMIT = 12;

function DashboardNewsCard({ article, brandStatsMap }) {
  const [imgOk, setImgOk] = useState(!!article.imageUrl);
  const sourceLabel = article.sourceName || article.sourceKey || 'News';
  const dateStr = formatNewsDateShort(article.publishedAt);
  const dateForAttr = newsArticleToJsDate(article.publishedAt);
  const brands = Array.isArray(article.extractedBrands) ? article.extractedBrands : [];
  const firstKey = brands[0];
  const stat = firstKey ? brandStatsMap[firstKey] : null;
  const listed =
    stat && typeof stat.activeListingCount === 'number' ? stat.activeListingCount : null;
  const displayName = stat?.displayName;

  const footer =
    displayName != null && listed != null ? (
      <Link
        to={`/search?q=${encodeURIComponent(displayName)}`}
        className="dashboard-pedal-news-card__footer"
      >
        {listed} {displayName} on PXC
      </Link>
    ) : null;

  return (
    <article className="dashboard-pedal-news-card">
      <a
        href={article.link}
        target="_blank"
        rel="noopener noreferrer"
        className="dashboard-pedal-news-card__thumb-wrap"
      >
        {article.imageUrl && imgOk ? (
          <img
            src={article.imageUrl}
            alt=""
            className="dashboard-pedal-news-card__thumb"
            loading="lazy"
            decoding="async"
            onError={() => setImgOk(false)}
          />
        ) : (
          <div className="dashboard-pedal-news-card__thumb-placeholder" aria-hidden>
            <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.25">
              <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
              <circle cx="8.5" cy="8.5" r="1.5" />
              <path d="M21 15l-5-5L5 21" />
            </svg>
          </div>
        )}
      </a>
      <div className="dashboard-pedal-news-card__body">
        <div className="dashboard-pedal-news-card__meta">
          <span className="dashboard-pedal-news-card__pill" title={sourceLabel}>
            {sourceLabel}
          </span>
          {dateStr ? (
            <time
              className="dashboard-pedal-news-card__date"
              dateTime={dateForAttr ? dateForAttr.toISOString() : undefined}
            >
              {dateStr}
            </time>
          ) : null}
        </div>
        <a
          href={article.link}
          target="_blank"
          rel="noopener noreferrer"
          className="dashboard-pedal-news-card__title"
        >
          {article.title || 'Untitled'}
        </a>
        {footer}
      </div>
    </article>
  );
}

function DashboardStripDivider() {
  return (
    <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 1.5rem' }}>
      <div style={{ height: '1px', backgroundColor: '#d1d5db', margin: '1rem 0' }} />
    </div>
  );
}

/**
 * Horizontal “Latest Pedal News” strip for the home dashboard.
 * Requires public read on `pedalNewsArticles` + `brandStats` (see firestore.rules).
 * @param {{ showDividerAbove?: boolean }} props — gray rule + spacing above strip (e.g. after Active Trades when logged in, or after Latest Pedals when logged out)
 */
export default function DashboardPedalNewsStrip({ showDividerAbove = false }) {
  const [articles, setArticles] = useState([]);
  const [brandStatsMap, setBrandStatsMap] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!isPedalNewsUiEnabled()) {
      setLoading(false);
      return;
    }

    let cancelled = false;

    (async () => {
      setLoading(true);
      try {
        const articlesSnap = await getDocs(
          query(
            collection(db, 'pedalNewsArticles'),
            orderBy('publishedAt', 'desc'),
            limit(FETCH_LIMIT)
          )
        );

        if (cancelled) return;

        const rows = articlesSnap.docs.map((d) => ({
          id: d.id,
          ...d.data()
        }));
        setArticles(rows);

        const stats = {};
        try {
          const statsSnap = await getDocs(collection(db, 'brandStats'));
          statsSnap.forEach((docSnap) => {
            stats[docSnap.id] = docSnap.data();
          });
        } catch (statsErr) {
          /* Optional enrichment; rules may allow articles but not brandStats for anonymous users */
          console.warn('Dashboard pedal news: brandStats unreadable, card footers hidden:', statsErr);
        }
        if (!cancelled) setBrandStatsMap(stats);
      } catch (e) {
        console.error('Dashboard pedal news: pedalNewsArticles query failed:', e);
        if (!cancelled) {
          setArticles([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  if (!isPedalNewsUiEnabled()) {
    return null;
  }

  if (loading) {
    return (
      <>
        {showDividerAbove ? <DashboardStripDivider /> : null}
        <section className="dashboard-pedal-news" aria-label="Latest Pedal News">
          <p className="dashboard-pedal-news__loading">Loading latest news…</p>
        </section>
      </>
    );
  }

  if (articles.length === 0) {
    return null;
  }

  return (
    <>
      {showDividerAbove ? <DashboardStripDivider /> : null}
      <section className="dashboard-pedal-news" aria-labelledby="dashboard-pedal-news-heading">
      <div className="dashboard-pedal-news__header-row">
        <div className="dashboard-pedal-news__title-block">
          <h2 id="dashboard-pedal-news-heading" className="dashboard-pedal-news__title">
            <Link to="/news" className="dashboard-pedal-news__title-link">
              Latest Pedal News
            </Link>
          </h2>
        </div>
        <Link to="/news" className="dashboard-pedal-news__see-all">
          See all →
        </Link>
      </div>
      <div className="dashboard-pedal-news__track">
        {articles.map((article) => (
          <DashboardNewsCard
            key={article.id}
            article={article}
            brandStatsMap={brandStatsMap}
          />
        ))}
      </div>
    </section>
    </>
  );
}
